import React, { useEffect, useRef } from "react";

/**
 * Common Modal
 * Props:
 *  - isOpen: boolean
 *  - title: string
 *  - children: ReactNode (body)
 *  - onClose: () => void
 *  - onConfirm?: () => Promise|void
 *  - confirmLabel?: string
 *  - confirmDisabled?: boolean
 */
export default function Modal({
  isOpen,
  title,
  children,
  onClose,
  onConfirm,
  confirmLabel = "Confirm",
  confirmDisabled = false,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      aria-modal="true"
      role="dialog"
      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2,6,23,0.45)",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onMouseDown={(e) => {
        // close when clicking overlay
        if (e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div
        ref={panelRef}
        className="modal-panel"
        style={{
          width: 640,
          maxWidth: "100%",
          background: "#fff",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(2,6,23,0.08)",
        }}
      >
        <div
          className="modal-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #f0f2f5",
          }}
        >
          <h5 style={{ margin: 0, color: "var(--brand-navy)", fontWeight: 700 }}>
            {title}
          </h5>
          <button
            aria-label="Close"
            onClick={() => onClose && onClose()}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              color: "var(--brand-slate)",
            }}
          >
            ×
          </button>
        </div>

        <div className="modal-body" style={{ padding: 20 }}>
          {children}
        </div>

        <div
          className="modal-footer"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 20px",
            borderTop: "1px solid #f0f2f5",
            background: "#fff",
          }}
        >
          <button
            type="button"
            className="btn btn-rescuern-secondary"
            onClick={() => onClose && onClose()}
            style={{ minWidth: 96 }}
          >
            Close
          </button>

          {onConfirm && (
            <button
              type="button"
              className="btn btn-rescuern-primary"
              onClick={onConfirm}
              disabled={confirmDisabled}
              style={{ minWidth: 110 }}
            >
              {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}